import { Injectable } from '@angular/core';
import { Observable, ReplaySubject, combineLatest, of } from 'rxjs';
import { map, share, switchMap } from 'rxjs/operators';
import { FirestoreService } from './firestore.service';
import { AREA_COLOR_PALETTE } from './annotation.constants';
import { CameraSnapshots, DetectionMode, RecordingSetup } from './types';

export interface SetupPreviewArea {
  points: string; // SVG polygon points, in pixels of the angle's image
  color: string;
}

export interface SetupPreviewAngle {
  cameraName: string;
  detectionModeName: string;
  imgUrl: string | null; // null when the camera has no snapshot
  width: number | null;
  height: number | null;
  areas: SetupPreviewArea[];
}

const ANGLES = [
  { camera: 'camera', snapshots: 'cameraSnapshots', detectionMode: 'detectionMode', areas: 'mainAreas' },
  { camera: 'endzoneCamera', snapshots: 'endzoneCameraSnapshots', detectionMode: 'endzoneDetectionMode', areas: 'endzoneMainAreas' },
  { camera: 'endzoneCamera2', snapshots: 'endzoneCameraSnapshots2', detectionMode: 'endzoneDetectionMode2', areas: 'endzoneMainAreas2' },
];

@Injectable({ providedIn: 'root' })
export class RecordingSetupPreviewService {
  private docs = new Map<string, Observable<any>>();

  constructor(private db: FirestoreService) {}

  /**
   * One entry per camera of the setup: the image its main areas are drawn on, and those areas.
   * Everything about the camera comes from the setup (its name and the snapshots copied on it): the camera
   * documents are not readable by team members.
   */
  getAngles(setupPath: string): Observable<SetupPreviewAngle[]> {
    return this.getDoc<RecordingSetup>(setupPath).pipe(
      switchMap((setup) => {
        const angles = ANGLES.filter((angle) => setup?.[angle.camera]?.v);
        if (angles.length === 0) {
          return of([]);
        }
        return combineLatest(angles.map((angle) => {
          const detectionModeRef = setup[angle.detectionMode]?.v;
          return (detectionModeRef ? this.getDoc<DetectionMode>(detectionModeRef.path) : of(null)).pipe(
            map((detectionMode) => this.buildAngle(setup[angle.camera].d, setup[angle.areas], setup[angle.snapshots] ?? null, detectionMode)),
          );
        }));
      }),
    );
  }

  /**
   * Previews of a practice plan mostly share a few setups and detection modes, so a document is listened to once
   * for all of them. The listener is dropped with its last subscriber: nothing stale is kept between modals.
   */
  private getDoc<T>(path: string): Observable<T | null> {
    let doc$ = this.docs.get(path);
    if (!doc$) {
      doc$ = this.db.getByPath<T>(path).pipe(share({ connector: () => new ReplaySubject<T | null>(1) }));
      this.docs.set(path, doc$);
    }
    return doc$;
  }

  private buildAngle(cameraName: string, areas: Array<{ points: Array<{ x: number; y: number }> }> | null, snapshots: CameraSnapshots | null, detectionMode: DetectionMode | null): SetupPreviewAngle {
    const detectionView = { imgUrl: snapshots?.detectionSnapshot, width: snapshots?.detectionResolutionWidth, height: snapshots?.detectionResolutionHeight };
    const cameraView = { imgUrl: snapshots?.snapshot, width: snapshots?.resolutionWidth, height: snapshots?.resolutionHeight };
    const hasAreas = Boolean(detectionMode?.requireMainArea);
    // Same pick as the recording setup form, so the areas land on the image they were drawn on.
    // A mode without areas runs no detection: the camera view is the closest to what gets recorded.
    const view = hasAreas
      ? (detectionView.imgUrl && detectionMode?.useDetectionMap !== false ? detectionView : cameraView)
      : (cameraView.imgUrl ? cameraView : detectionView);
    const hasSize = Boolean(view.width && view.height);

    return {
      cameraName: cameraName ?? '',
      detectionModeName: detectionMode?.name ?? '',
      imgUrl: view.imgUrl || null,
      width: hasSize ? view.width : null,
      height: hasSize ? view.height : null,
      areas: hasAreas && hasSize && Array.isArray(areas)
        ? areas
          .map((area, index) => ({ points: area?.points ?? [], color: AREA_COLOR_PALETTE[index % AREA_COLOR_PALETTE.length] }))
          .filter((area) => area.points.length > 2)
          .map((area) => ({ points: area.points.map((point) => `${point.x},${point.y}`).join(' '), color: area.color }))
        : [],
    };
  }
}
